"use client";
import Link from "next/link";
import clsx from "clsx";
import React, { useState, FC } from "react";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import { AnimatePresence, LazyMotion, m } from "motion/react";
import { ChevronRight, ChevronDown, ChevronUp } from "lucide-react";
import MaxWidthWrapper from "components/Tiles/commonComponents/MaxWidthWrapper";
import BlurImage from "components/ui/blur-image";
import useMediaQuery from "components/Tiles/hooks/useMediaQuery";
import { getBlurUrl } from "components/Image/utils";
import { getButtonVariantStylesAndSize, ButtonVariantTypes } from "components/ui/button";
import "./style.css";

const loadFeatures = () => import("components/Tiles/utils/motionFeatures").then((res) => res.default);

interface Feature {
  accordionTitle: string;
  description: string;
  icon?: string;
  slug?: string;
  linkTitle?: string;
  key?: string;
  thumbnail: string;
  title: string;
}

interface FeaturesProps {
  features: Feature[];
  title: string;
  subtitle: string;
  bgColor?: string;
  flipLayout?: boolean;
}

const Features: FC<FeaturesProps> = ({ features, title, subtitle, flipLayout = false, bgColor }) => {
  const [activeFeature, setActiveFeature] = useState<number>(0);
  const { isMobile } = useMediaQuery();

  return (
    <section style={{ backgroundColor: bgColor }} id="Features-98">
      <div id="features">
        <MaxWidthWrapper className="max-w-[1200px] pb-10 pt-24 ">
          <div className="tile-title-container ">
            <h2 className="tile-main-header font-heading [text-wrap:balance] ">{title}</h2>
            <p className="tile-subtitle [text-wrap:balance]">{subtitle}</p>
          </div>

          <div className="mb-8 mt-4 w-full rounded-2xl ">
            <div className="grid grid-cols-1 items-center gap-10 p-0 lg:grid-cols-2 lg:gap-32 lg:py-12">
              <div className="order-last w-full">
                {features.map((feature, index) => (
                  <Disclosure key={feature.key}>
                    {({ open }) => (
                      <>
                        {" "}
                        <DisclosureButton
                          onClick={() => {
                            setActiveFeature(index);
                          }}
                          className="w-full"
                        >
                          <div
                            className={clsx(
                              "flex w-full flex-row items-start justify-between space-x-3 p-3 pl-0 text-left lg:pl-3",
                              open ? "text-primary-700" : "text-gray-600"
                            )}
                          >
                            <h3 className="text-2xl font-semibold leading-none lg:leading-normal ">
                              {feature.accordionTitle}
                            </h3>
                            {open ? (
                              <ChevronUp size={20} strokeWidth={2.5} />
                            ) : (
                              <ChevronDown size={20} strokeWidth={2.5} />
                            )}
                          </div>
                        </DisclosureButton>
                        <DisclosurePanel>
                          {isMobile && (
                            <div className="relative h-[328px] w-[328px] overflow-hidden rounded-3xl">
                              <BlurImage
                                src={feature.thumbnail}
                                placeholder="blur"
                                blurDataURL={getBlurUrl()}
                                alt={feature.title}
                                fill
                              />
                            </div>
                          )}
                          <div className="p-3 pl-0 pt-3 lg:pl-3 lg:pt-0">
                            <p className="mb-2 mt-2 text-base text-primary-700 lg:mt-0">{feature.description}</p>
                            {feature.slug && feature.linkTitle && (
                              <Link
                                href={`/features/${feature.slug}`}
                                className={clsx(
                                  getButtonVariantStylesAndSize({ variant: ButtonVariantTypes.BRAND_LINK }),
                                  "items-center pl-0 text-base"
                                )}
                              >
                                {feature.linkTitle} <ChevronRight size={20} strokeWidth={2.5} />
                              </Link>
                            )}
                          </div>
                        </DisclosurePanel>
                      </>
                    )}
                  </Disclosure>
                ))}
              </div>
              <div
                className={clsx(
                  "hidden items-center justify-center self-center text-center align-middle lg:col-span-1 lg:flex",
                  flipLayout && "order-last"
                )}
              >
                <LazyMotion features={loadFeatures}>
                  <AnimatePresence mode="wait">
                    {features.map((feature, index) => {
                      if (index === activeFeature) {
                        return (
                          <m.div
                            key={feature.key || index}
                            initial={{
                              y: 10,
                              opacity: 0
                            }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{
                              y: -10,
                              opacity: 0
                            }}
                            transition={{
                              duration: 0.15,
                              stiffness: 300,
                              damping: 30
                            }}
                            className="feature-container relative h-[630px] overflow-hidden  rounded-3xl"
                          >
                            <BlurImage
                              src={feature.thumbnail}
                              placeholder="blur"
                              blurDataURL={getBlurUrl()}
                              alt={feature.title}
                              className="features-blur-image rounded-3xl object-contain"
                              fill
                              style={{
                                objectFit: "cover" // cover, contain, none
                              }}
                            />
                          </m.div>
                        );
                      }
                    })}
                  </AnimatePresence>
                </LazyMotion>
              </div>
            </div>
          </div>
        </MaxWidthWrapper>
      </div>
    </section>
  );
};

export default Features;
